import React, { useState } from 'react';
import { Mail, Edit, Trash2, Plus, X, Search, User as UserIcon, Phone, UserPlus, Copy, Check, Building, DollarSign } from 'lucide-react';
import { TextInput } from '../../../shared/components/TextInput';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { UserRegistrationForm } from '../components/UserRegistrationForm';
import { SalarioColaboradorModal } from '../components/SalarioColaboradorModal';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { FEATURES } from '../../../shared/lib/permissions';
import { extractEmailDomain, maskPhone } from '../../../shared/utils/validators';
import { useUsuarios, useEmpresasParaSelect, useCriarUsuario, useAtualizarUsuario, useAtribuirGestor } from '../hooks';
import { useFornecedores } from '../../compras-fornecedores/hooks';
import { useRoleLabels } from '../../../shared/hooks/useRoleLabels';
import type { User } from '../../../shared/lib/api';

const UsersPage: React.FC = () => {
  const { labelFor: labelForRole } = useRoleLabels();
  const { can } = usePermissions();
  const [salarioDe, setSalarioDe] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [successIsWarning, setSuccessIsWarning] = useState(false);
  const [passwordSetupLink, setPasswordSetupLink] = useState<{ url: string; expiresInHours: number } | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    companyId: '',
  });
  const [editError, setEditError] = useState<string | null>(null);
  const [showConfirmEdit, setShowConfirmEdit] = useState(false);
  const [assignTarget, setAssignTarget] = useState<User | null>(null);
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [assignError, setAssignError] = useState<string | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);

  const { data: usersData, isLoading, isError, error } = useUsuarios({ limit: 100 });
  const { data: companiesData } = useEmpresasParaSelect();
  const { data: suppliersData } = useFornecedores({ limit: 100 });
  const criarUsuario = useCriarUsuario();
  const atualizarUsuario = useAtualizarUsuario();
  const atribuirGestor = useAtribuirGestor();

  const users: User[] = usersData?.data ?? [];
  const companies = companiesData ?? [];
  const suppliers = suppliersData?.data ?? [];

  const handleEditClick = (user: User) => {
    setEditUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      phone: user.phone || '',
      companyId: user.companyId || '',
    });
    setEditError(null);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditUser(null);
    setEditError(null);
    setShowConfirmEdit(false);
  };

  const validateEditForm = () => {
    if (!editForm.name.trim()) return 'Nome completo é obrigatório';
    if (!editForm.email.trim()) return 'E-mail é obrigatório';
    if (!editForm.phone.trim()) return 'Telefone é obrigatório';
    if (
      (editUser?.role === 'manager' || editUser?.role === 'collaborator') &&
      !editForm.companyId
    ) return 'Empresa é obrigatória para os perfis Gestor e Colaborador';
    return null;
  };

  const handleEditSave = () => {
    const validation = validateEditForm();
    if (validation) { setEditError(validation); return; }
    setShowConfirmEdit(true);
  };

  const handleEditConfirm = async () => {
    if (!editUser) return;
    try {
      const data = editUser.role === 'manager' || editUser.role === 'collaborator'
        ? { name: editForm.name, email: editForm.email, phone: editForm.phone, companyId: editForm.companyId }
        : { name: editForm.name, email: editForm.email, phone: editForm.phone };
      await atualizarUsuario.mutateAsync({ id: editUser.id, data });
      setSuccessMessage('Alteração realizada com sucesso!');
      closeEditModal();
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      const rawMessage = err?.response?.data?.message;
      const message = Array.isArray(rawMessage) ? rawMessage.join('. ') : rawMessage;
      setEditError(message || err?.message || 'Erro ao atualizar usuário.');
    }
  };

  const handleCreateUser = async (userData: any) => {
    const created = await criarUsuario.mutateAsync(userData);
    // O cadastro é concluído mesmo se o e-mail falhar — avisamos para que o admin
    // comunique o acesso por outro canal.
    const emailFailed = created?.welcomeEmailSent === false;
    const setupLink = created?.passwordSetupLink ?? null;

    setSuccessIsWarning(emailFailed);
    setSuccessMessage(
      emailFailed
        ? setupLink
          ? 'Usuário cadastrado, mas o e-mail de boas-vindas não pôde ser enviado. Copie o link abaixo e envie ao usuário para que ele defina a senha.'
          : 'Usuário cadastrado, mas o e-mail de boas-vindas não pôde ser enviado. Oriente o usuário a usar "Esqueci minha senha" na tela de login para definir o acesso.'
        : 'Usuário cadastrado com sucesso!',
    );
    setPasswordSetupLink(setupLink);
    setLinkCopied(false);
    setShowModal(false);

    // Com link na tela não usamos auto-dismiss: ele existe uma vez só e o admin
    // precisa copiá-lo antes que o aviso suma.
    if (!setupLink) {
      setTimeout(() => setSuccessMessage(null), 8000);
    }
  };

  const handleCopySetupLink = async () => {
    if (!passwordSetupLink) return;
    try {
      await navigator.clipboard.writeText(passwordSetupLink.url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    } catch {
      // Sem permissão de clipboard (http, navegador antigo): o link segue
      // visível e selecionável no campo ao lado.
      setLinkCopied(false);
    }
  };

  const handleDismissSuccess = () => {
    setSuccessMessage(null);
    setPasswordSetupLink(null);
    setLinkCopied(false);
  };

  const handleOpenAssign = (user: User) => {
    setAssignTarget(user);
    setSelectedManagerId(user.managerId ?? '');
    setAssignError(null);
  };

  const handleAssignConfirm = async () => {
    if (!assignTarget || !selectedManagerId) {
      setAssignError('Selecione um gestor');
      return;
    }
    setIsAssigning(true);
    try {
      await atribuirGestor.mutateAsync({ collaboratorId: assignTarget.id, managerId: selectedManagerId });
      setAssignTarget(null);
      setSelectedManagerId('');
      setSuccessMessage('Gestor atribuído com sucesso!');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setAssignError(err?.response?.data?.message || err?.message || 'Erro ao atribuir gestor.');
    } finally {
      setIsAssigning(false);
    }
  };

  const getCorporateDomains = () =>
    companies.map(c => extractEmailDomain(c.responsibleEmail)).filter(Boolean) as string[];

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const managersForCompany = (companyId: string | null) =>
    users
      .filter(u => u.role === 'manager' && u.companyId === companyId)
      .map(u => ({ value: u.id, label: u.name }));

  const getManagerName = (managerId: string | null) =>
    managerId ? users.find(u => u.id === managerId)?.name ?? '—' : '—';

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Usuários</h1>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Novo Usuário
        </button>
      </div>

      {successMessage && (
        <div
          className={`p-4 rounded-lg border ${
            successIsWarning
              ? 'bg-amber-50 border-amber-200'
              : 'bg-green-50 border-green-200'
          }`}
        >
          <div className="flex items-start justify-between gap-4">
            <p className={`text-sm ${successIsWarning ? 'text-amber-700' : 'text-green-600'}`}>
              {successMessage}
            </p>
            {passwordSetupLink && (
              <button
                type="button"
                onClick={handleDismissSuccess}
                className="text-amber-500 hover:text-amber-700 flex-shrink-0"
                aria-label="Fechar aviso"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {passwordSetupLink && (
            <div className="mt-3">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  readOnly
                  value={passwordSetupLink.url}
                  onFocus={(e) => e.currentTarget.select()}
                  className="flex-1 min-w-0 px-3 py-2 text-xs font-mono bg-white border border-amber-300 rounded-lg text-gray-700"
                  aria-label="Link de primeiro acesso"
                />
                <button
                  type="button"
                  onClick={handleCopySetupLink}
                  className="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors flex-shrink-0"
                >
                  {linkCopied ? (
                    <>
                      <Check className="w-4 h-4 mr-1" />
                      Copiado
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-1" />
                      Copiar link
                    </>
                  )}
                </button>
              </div>
              <p className="mt-2 text-xs text-amber-700">
                O link expira em {passwordSetupLink.expiresInHours}h, vale um único uso e não será
                exibido novamente. Se perder, use "Esqueci minha senha" na tela de login para gerar outro.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="max-w-md mb-4">
        <TextInput
          label="Buscar usuários"
          icon={<Search className="w-5 h-5" />}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div>Carregando...</div>
      ) : isError ? (
        <div className="text-red-600">Erro: {(error as Error)?.message}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">E-mail</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Perfil</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Regime</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gestor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map(user => (
                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{user.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-900">
                      <Mail className="w-4 h-4 mr-2 text-gray-400" />
                      {user.email}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {labelForRole(user.role)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.role === 'collaborator' && user.employmentType ? (
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          user.employmentType === 'pj'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {user.employmentType.toUpperCase()}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.role === 'collaborator'
                      ? getManagerName(user.managerId ?? null)
                      : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex space-x-2">
                      <button
                        className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded transition-colors"
                        onClick={() => handleEditClick(user)}
                        title="Editar usuário"
                        aria-label={`Editar usuário ${user.name}`}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {user.employmentType === 'pj' && can(FEATURES.pessoasSalarios) && (
                        <button
                          className="p-1 text-emerald-600 hover:text-emerald-900 hover:bg-emerald-50 rounded transition-colors"
                          title="Salário"
                          aria-label={`Salário de ${user.name}`}
                          onClick={() => setSalarioDe(user)}
                        >
                          <DollarSign className="w-4 h-4" />
                        </button>
                      )}
                      {user.role === 'collaborator' && (
                        <button
                          className="p-1 text-purple-600 hover:text-purple-900 hover:bg-purple-50 rounded transition-colors"
                          title="Atribuir gestor"
                          onClick={() => handleOpenAssign(user)}
                        >
                          <UserPlus className="w-4 h-4" />
                        </button>
                      )}
                      <button className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {salarioDe && <SalarioColaboradorModal colaborador={salarioDe} onClose={() => setSalarioDe(null)} />}

      {/* Modal de edição de usuário */}
      {showEditModal && editUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Editar Usuário</h2>
              <button onClick={closeEditModal} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-6 space-y-4">
              <TextInput label="Nome Completo *" type="text" icon={<UserIcon className="w-5 h-5" />} value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
              <TextInput label="E-mail *" type="email" icon={<Mail className="w-5 h-5" />} value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
              <TextInput label="Telefone *" type="text" icon={<Phone className="w-5 h-5" />} value={editForm.phone} inputMode="tel" onChange={e => setEditForm(f => ({ ...f, phone: maskPhone(e.target.value) }))} />
              {(editUser.role === 'manager' || editUser.role === 'collaborator') && (
                <AutocompleteSelect
                  label="Empresa *"
                  icon={<Building className="w-5 h-5" />}
                  options={companies.map(company => ({
                    value: company.id,
                    label: company.tradeName || company.corporateName,
                  }))}
                  value={editForm.companyId}
                  onChange={companyId => setEditForm(form => ({ ...form, companyId }))}
                />
              )}
              <div className="flex space-x-4">
                <div className="flex-1">
                  <TextInput label="Perfil" icon={<UserIcon className="w-5 h-5" />} value={labelForRole(editUser.role)} disabled />
                </div>
                <div className="flex-1">
                  <TextInput label="CPF" value={editUser.cpf ?? ''} disabled />
                </div>
                <div className="flex-1">
                  <TextInput label="RG" value={editUser.rg ?? ''} disabled />
                </div>
              </div>
              {editError && <div className="text-red-600 text-sm">{editError}</div>}
              <div className="flex justify-end space-x-2 mt-4">
                <button className="px-4 py-2 rounded bg-gray-200 text-gray-700" onClick={closeEditModal}>Cancelar</button>
                <button className="px-4 py-2 rounded bg-blue-600 text-white" onClick={handleEditSave}>Salvar</button>
              </div>
            </div>
            {showConfirmEdit && (
              <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl p-8 max-w-sm w-full">
                  <p className="text-lg font-semibold mb-6">Deseja Confirmar a Alteração?</p>
                  <div className="flex justify-end space-x-2">
                    <button className="px-4 py-2 rounded bg-gray-200 text-gray-700" onClick={() => setShowConfirmEdit(false)}>Cancelar</button>
                    <button className="px-4 py-2 rounded bg-blue-600 text-white" onClick={handleEditConfirm}>Confirmar</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de cadastro de usuário */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Cadastrar Novo Usuário</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-6">
              <UserRegistrationForm
                onSubmit={handleCreateUser}
                companies={companies}
                managers={users.filter(u => u.role === 'manager').map(u => ({ id: u.id, name: u.name, companyId: u.companyId ?? null }))}
                suppliers={suppliers.map(s => ({ id: s.id, name: s.tradeName || s.corporateName, documentNumber: s.documentNumber }))}
                corporateDomains={getCorporateDomains()}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de atribuição de gestor */}
      {assignTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Atribuir Gestor</h2>
              <button onClick={() => setAssignTarget(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Selecione o gestor para <span className="font-semibold text-gray-900">{assignTarget.name}</span>.
              </p>
              <AutocompleteSelect
                label="Gestor *"
                icon={<UserIcon className="w-5 h-5" />}
                options={managersForCompany(assignTarget.companyId ?? null)}
                value={selectedManagerId}
                onChange={setSelectedManagerId}
                error={assignError ?? undefined}
              />
              {assignError && (
                <p className="text-sm text-red-600">{assignError}</p>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setAssignTarget(null)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAssignConfirm}
                  disabled={isAssigning}
                  className={`px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors ${isAssigning ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isAssigning ? 'Atribuindo...' : 'Atribuir'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
